// The measured point's grid reference in the walker's chosen format, ported from the Android app's
// MeasureGridReference.kt (plus the lettering in its Proj4Manager). Null means the point is outside
// that grid: the sheet then shows "- -" and Copy is disabled.
//
// Irish Grid and OSGB project through PROJ with the app's own proj strings, exactly as Android
// (proj4j) and iOS (PROJ) do, then are lettered and truncated to the metre here. ITM, UTM and
// MGRS are pure maths.

import proj4 from 'proj4';
import { GridRef, GridRefUtils } from '../../../utils/helpers/GridRefPreference';
import { formatFixed, floorMod, padInteger } from './numbers';
import { ITM, UtmCoordinate, formatUtm, toUtm } from './transverseMercator';

/** The grid the measured point's reference is written in. Android's enum names, so stored values match. */
export type MeasureGridFormat = 'IrishGrid' | 'Itm' | 'Osgb' | 'Utm' | 'Mgrs' | 'LatLong';

/** Every format, in Android's enum order (the settings list order). */
export const MEASURE_GRID_FORMATS: readonly MeasureGridFormat[] = ['IrishGrid', 'Itm', 'Osgb', 'Utm', 'Mgrs', 'LatLong'];

/** A stored format name, or null when it is not one (never picked, or from a newer build). */
export function gridFormatFromStored(raw: string | null | undefined): MeasureGridFormat | null {
  return MEASURE_GRID_FORMATS.find((format) => format === raw) ?? null;
}

/**
 * Until the walker picks a format for the tool, follow the app-wide coordinate preference: Irish
 * Grid users get Irish Grid, British grid users OSGB, everyone else Lat/Long. The measure tool has
 * no NZ grid on either app, so NZ grid users get Lat/Long too.
 */
export function defaultGridFormat(appGridRef: GridRef): MeasureGridFormat {
  switch (appGridRef) {
    case GridRef.IeGrid: return 'IrishGrid';
    case GridRef.GbGrid: return 'Osgb';
    default: return 'LatLong';
  }
}

/** The stored choice, else the one that matches the app-wide grid reference preference. */
export function resolveGridFormat(stored: string | null | undefined, appGridRef: GridRef): MeasureGridFormat {
  return gridFormatFromStored(stored) ?? defaultGridFormat(appGridRef);
}

const IRISH_GRID_EXTENT = 500_000;
const OSGB_EASTING_EXTENT = 700_000.0;
const OSGB_NORTHING_EXTENT = 1_300_000.0;

interface EastingNorthingPair {
  x: number;
  y: number;
}

type Projector = (lngLat: [number, number]) => [number, number];
const projectors = new Map<GridRef, Projector>();

/** WGS84 to the app's Irish or British grid, with the same proj strings as the coords bar. */
function projectToNationalGrid(gridRef: GridRef, latitude: number, longitude: number): EastingNorthingPair {
  let projector = projectors.get(gridRef);
  if (!projector) {
    const converter = proj4(String(GridRefUtils.toProjection(GridRef.LatLon)), String(GridRefUtils.toProjection(gridRef)));
    projector = (lngLat) => converter.forward(lngLat) as [number, number];
    projectors.set(gridRef, projector);
  }
  const [x, y] = projector([longitude, latitude]);
  return { x, y };
}

const IRISH_LETTERS: Record<string, string> = {
  '0 0': 'V', '1 0': 'W', '2 0': 'X', '3 0': 'Y', '4 0': 'Z',
  '0 1': 'Q', '1 1': 'R', '2 1': 'S', '3 1': 'T', '4 1': 'U',
  '0 2': 'L', '1 2': 'M', '2 2': 'N', '3 2': 'O', '4 2': 'P',
  '0 3': 'F', '1 3': 'G', '2 3': 'H', '3 3': 'J', '4 3': 'K',
  '0 4': 'A', '1 4': 'B', '2 4': 'C', '3 4': 'D', '4 4': 'E',
};

/** Android Proj4Manager.getLetterFromIrishGridRef: the 100 km letter and 5-figure easting/northing. */
export function irishGridLetters(x: number, y: number): string {
  // The leading digit of the printed number is the 100 km square.
  const first = x < 100000 ? 0 : Number(String(x)[0]);
  const second = y < 100000 ? 0 : Number(String(y)[0]);
  const letter = IRISH_LETTERS[`${first} ${second}`] ?? '-';
  const figures = (value: number): string => (value >= 100000
    ? padInteger(parseInt(String(Math.round(value)).slice(1), 10), 5)
    : padInteger(Math.round(value), 5));
  return `${letter} ${figures(x)} ${figures(y)}`;
}

// Java's Character.isLetter. Built at runtime so the TS target need not know the `u` flag.
const UNICODE_LETTER = new RegExp('^\\p{L}$', 'u');

/**
 * Android Proj4Manager.getLetterFromUKGridRef: the two-letter 100 km square and [digits] figures
 * (8 for the coords bar, 10 for the measure tool), or "- -" off the grid.
 */
export function ukGridLetters(eastingX: number, northingY: number, digits = 8): string {
  const e100km = Math.floor(eastingX / 100_000);
  const n100km = Math.floor(northingY / 100_000);

  let l1 = (19 - n100km) - (19 - n100km) % 5 + Math.floor((e100km + 10) / 5);
  let l2 = (19 - n100km) * 5 % 25 + e100km % 5;
  if (l1 > 7) l1 += 1;
  if (l2 > 7) l2 += 1;
  if (l2 < 0 || l1 < 0) return '- -';

  const char1 = String.fromCharCode(Math.trunc(l1) + 65);
  const char2 = String.fromCharCode(Math.trunc(l2) + 65);

  const e = Math.floor((eastingX % 100_000) / Math.pow(10, 5 - digits / 2));
  const n = Math.floor((northingY % 100_000) / Math.pow(10, 5 - digits / 2));
  // Zero-padded to half the figures: 4 for an 8-figure reference, 5 for a 10-figure one.
  const width = Math.max(1, Math.trunc(digits / 2));
  const formattedE = padInteger(Math.trunc(Math.floor(e % 1_000_000)), width);
  const formattedN = padInteger(Math.trunc(Math.floor(n % 1_000_000)), width);

  if (!UNICODE_LETTER.test(char1) || !UNICODE_LETTER.test(char2)) return '- -';
  if (Math.round(e) < 0 || Math.round(n) < 0) return '- -';
  return `${char1}${char2} ${formattedE} ${formattedN}`;
}

/** The converters print "-" in place of a square letter (or "- -") when a point is off the grid. */
function isLettered(value: string): boolean {
  return !value.split(' ').some((part) => part === '-') && /[0-9]/.test(value);
}

/**
 * "T 29394 95276" from Irish Grid metres. Truncated to the metre, like the other grids here and on
 * iOS: a reference names the 1 m square the point is in. Inside the 500 km grid only.
 */
export function irishGridFromEastingNorthing(easting: number, northing: number): string | null {
  if (!Number.isFinite(easting) || !Number.isFinite(northing)) return null;
  const e = Math.floor(easting);
  const n = Math.floor(northing);
  if (e < 0 || e >= IRISH_GRID_EXTENT || n < 0 || n >= IRISH_GRID_EXTENT) return null;
  const text = irishGridLetters(e, n);
  return isLettered(text) ? text : null;
}

/** "NN 16689 71275": 10 figures, inside the 700 × 1300 km British National Grid only. */
export function osgbFromEastingNorthing(easting: number, northing: number): string | null {
  if (!(easting >= 0 && easting < OSGB_EASTING_EXTENT && northing >= 0 && northing < OSGB_NORTHING_EXTENT)) return null;
  const text = ukGridLetters(easting, northing, 10);
  return isLettered(text) ? text : null;
}

export function irishGrid(latitude: number, longitude: number): string | null {
  const projected = projectToNationalGrid(GridRef.IeGrid, latitude, longitude);
  return irishGridFromEastingNorthing(projected.x, projected.y);
}

export function osgb(latitude: number, longitude: number): string | null {
  const projected = projectToNationalGrid(GridRef.GbGrid, latitude, longitude);
  return osgbFromEastingNorthing(projected.x, projected.y);
}

/** "728641 692424": whole metres, within ITM's area of use (the island of Ireland, with a margin). */
export function itm(latitude: number, longitude: number): string | null {
  if (latitude < 51.0 || latitude > 56.0 || longitude < -11.0 || longitude > -5.0) return null;
  const projected = ITM.project(latitude, longitude);
  return `${Math.round(projected.easting)} ${Math.round(projected.northing)}`;
}

const MGRS_COLUMN_SETS = ['ABCDEFGH', 'JKLMNPQR', 'STUVWXYZ'];
const MGRS_ROW_LETTERS = 'ABCDEFGHJKLMNPQRSTUV';

/**
 * Military Grid Reference System at 1 m from a UTM position: "31N AA 66021 00000". The 100 km
 * column letter cycles through three sets by zone, the row letter through A–V with even zones
 * offset by five (the "AA" lettering WGS84 uses); figures are truncated, as UTM's are.
 */
export function mgrsFromUtm(utm: UtmCoordinate): string | null {
  const columns = MGRS_COLUMN_SETS[(utm.zone - 1) % 3];
  const column100k = Math.floor(utm.easting / 100_000);
  if (column100k < 1 || column100k > columns.length) return null;
  const row100k = Math.floor(utm.northing / 100_000);
  const rowIndex = floorMod(row100k + (utm.zone % 2 === 0 ? 5 : 0), MGRS_ROW_LETTERS.length);
  const easting = floorMod(Math.floor(utm.easting), 100_000);
  const northing = floorMod(Math.floor(utm.northing), 100_000);
  return `${utm.zone}${utm.band} ${columns[column100k - 1]}${MGRS_ROW_LETTERS[rowIndex]} ` +
    `${padInteger(easting, 5)} ${padInteger(northing, 5)}`;
}

/** MGRS for a WGS84 point; undefined outside 80°S–84°N, where the polar UPS grid takes over. */
export function mgrs(latitude: number, longitude: number): string | null {
  const utm = toUtm(latitude, longitude);
  return utm ? mgrsFromUtm(utm) : null;
}

/** "52.99290, -6.07370": five decimals, always a dot. */
export function latLong(latitude: number, longitude: number): string {
  return `${formatFixed(latitude, 5)}, ${formatFixed(longitude, 5)}`;
}

/** The reference text for a point in [format], or null when the point is outside that grid. */
export function formatGridReference(format: MeasureGridFormat, lat: number, lng: number): string | null {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  try {
    switch (format) {
      case 'IrishGrid': return irishGrid(lat, lng);
      case 'Itm': return itm(lat, lng);
      case 'Osgb': return osgb(lat, lng);
      case 'Utm': {
        const utm = toUtm(lat, lng);
        return utm ? formatUtm(utm) : null;
      }
      case 'Mgrs': return mgrs(lat, lng);
      case 'LatLong': return latLong(lat, lng);
      default: return null;
    }
  } catch {
    // A projection that throws (as Android's runCatching guards) just means "no reference here".
    return null;
  }
}
